import { defineComponent as _defineComponent } from 'vue'
import ReportConstructorWrap from "@/components/statistics/extended-report/ReportConstructorWrap.vue";
import ColumnsCheckboxes from "@/components/statistics/ColumnsCheckboxes.vue";
import { computed, onBeforeMount, ref, watch } from "vue";
import TrashBtn from "@/components/base/buttons/TrashBtn.vue";
import { PostbacksColumns, PostbacksFilters } from "@/services/TablePage/postbacks";
import FilterSubIds from "@/components/statistics/extended-report/FilterSubIds.vue";
import FiltersDesign from "@/components/base/filters/FiltersDesign.vue";
import { useRoute, useRouter } from "vue-router/composables";
import store from "@/store";
import PageTabs from "@/components/base/PageTabs.vue";
import { useWindowResize } from "@/utils/useWindowSize";
import { getEmployeesCountries, getOneEmployeesOffer } from "@/api/offers";
import { showServerError } from "@/utils";
import { IColumnFilter } from "@/types";
import { getCurrentDate } from "@/utils/formatDates";
import ReportFilterByDate from "@/components/statistics/extended-report/ReportFilterByDate.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'PostbacksConstructor',
  emits: ['submit', 'clear'],
  setup(__props, { expose, emit }) {

const route = useRoute();
const router = useRouter();
const selected = ref<string[]>([]);
const filters = ref<PostbacksFilters>({});
const columns = ref<IColumnFilter[]>([]);
const filtersComp = ref<FiltersDesign>(null);



const currentTab = ref(0);
const tabs = [
    { tab: 'Колонки' },
    { tab: 'Фильтры' },
];

const changeTab = (tab: number) => currentTab.value = tab;

const { width } = useWindowResize();
const isMobile = computed(() => width.value < 577);

const getDateFilter = computed(() => {
    return filters.value.filters.find((filter) => filter.id === 'postback_date');
});

const getInitialFiltersFromQuery = (): Record<string, any> => {
    const today = getCurrentDate('yyyy-MM-dd');
    const q = route.query;
    const toArray = (v: any): any[] => Array.isArray(v) ? v : [v];
    const params: Record<string, any> = {
        postback_date_from: (q.postback_date_from as string) || today,
        postback_date_to: (q.postback_date_to as string) || today,
    };
    if (q.error_types) params.error_types = toArray(q.error_types);
    if (q.offer_ids) params.offer_ids = toArray(q.offer_ids);
    if (q.advertiser_ids) params.advertiser_ids = toArray(q.advertiser_ids);
    if (q.advertiser_manager_ids) params.advertiser_manager_ids = toArray(q.advertiser_manager_ids);
    return params;
};

onBeforeMount(() => {
    // Читаем фильтры из query (переход со списка ошибок: период, тип ошибки, оффер,
    // рекламодатель, AM) и кладём в store до первого запроса отчёта. Селекты в самом
    // конструкторе заполнит parseRoute из FiltersDesign при открытии диалога (он lazy).
    store.commit('SET_FILTERS_PARAMS_OBJECT', getInitialFiltersFromQuery());
    filters.value = new PostbacksFilters();
    columns.value = new PostbacksColumns().columns;
    prepareFilters();
    filters.value.getErrorTypes();
    setDefaultColumns();
    updateSelectedColumns();
    updateUrl();
});

const setDefaultColumns = () => {
    selected.value = columns.value.flatMap(i => i.items.filter(j => j.default).map(k => k.value));
    selected.value.unshift('postback_id');
};

const updateSelectedColumns = () => {
    const columns = route.query.columns;
    if (!columns || (Array.isArray(columns) && columns.length === 0)) return;
    selected.value = columns;
};

watch(() => route.query, () => {
    updateUrl();
});

const updateUrl = () => {
    const data = { columns: [...selected.value] };
    const query = Object.keys(route.query)?.length > 0 ? { ...route.query, ...data } : data;
    router.replace({ name: router.currentRoute.name || '', query }).catch((error) => {
        if (error.name !== 'NavigationDuplicated') {
            throw error;
        }
    });
};

const loadingDataForSelect = [
    { id: 'click_geo', errorName: 'Список стран не загружен', api: getEmployeesCountries },
];

const prepareFilters = () => {
    for (const select of loadingDataForSelect) {
        getDataForSelect(select.api, select.id, select.errorName);
    }
};

const getDataForSelect = async (apiMethod: any, filterId: string, errorName: string = 'Список не загружен') => {
    const filter = filters.value.filters.find(i => i.id === filterId);
    if (!filter) {
        return;
    }
    try {
        const items = await apiMethod();
        filter.items = items.data || items;
    } catch (err) {
        showServerError(err, errorName);
    }
};

const submitFilters = () => {
    filtersComp.value.submit();
};

const currentOffer = computed(() => {
    if (!filters.value.filters) return [];
    const offerFilter = filters.value.filters.find((filter) => filter.id === 'offer_ids');
    return offerFilter ? offerFilter.select : [];
});

const getGoalFilter = computed(() => {
    return filters.value.filters.find((filter) => filter.id === 'goal_ids');
});

watch(() => currentOffer.value, () => {
    if (!filters.value.filters) return;
    getGoalFilter.value!.disabled = currentOffer.value.length <= 0;
    if (currentOffer.value.length <= 0) {
        getGoalFilter.value!.select = [];
    }
    if (getGoalFilter.value && currentOffer.value.length > 1) {
        getGoalFilter.value!.tooltip = 'Фильтр по цели возможен только если отчёт отфильтрован по одному офферу';
    }
    if (getGoalFilter.value && currentOffer.value.length === 1) {
        getGoals(currentOffer.value[0].legacy_id);
    }
}, { deep: true });

const getGoals = async (id: number) => {
    try {
        const { goals } = await getOneEmployeesOffer(id, { by_legacy_id: 1 });
        getGoalFilter.value!.items = goals;
        if (route.query.hasOwnProperty('goal_ids') && getGoalFilter.value!.select.length === 0) {
            const goalIds = route.query.goal_ids;
            if (!goalIds || goalIds === 'NaN') return;
            getGoalFilter.value!.select = Array.isArray(goalIds) ? goalIds.map(g => +g!) : [Number(goalIds)];
        }
    } catch (e) {
        showServerError(e, 'Список целей оффера не загружен');
    }
};

const clearFilters = () => {
    filters.value.clearFilters();
    store.commit('SET_FILTERS_PARAMS_OBJECT', {});
    emit('clear');
};

const clearAll = () => {
    filters.value.clearFilters();
    store.commit('SET_FILTERS_PARAMS_OBJECT', {});
    setDefaultColumns();
    emit('clear');
};

expose({ selected });

return { __sfc: true,route, router, selected, filters, columns, filtersComp, emit, currentTab, tabs, changeTab, width, isMobile, getDateFilter, getInitialFiltersFromQuery, setDefaultColumns, updateSelectedColumns, updateUrl, loadingDataForSelect, prepareFilters, getDataForSelect, submitFilters, currentOffer, getGoalFilter, getGoals, clearFilters, clearAll, ReportConstructorWrap, ColumnsCheckboxes, TrashBtn, FilterSubIds, FiltersDesign, PageTabs, ReportFilterByDate }
}

})